import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getProjectById, updateProject } from "../../services/projectService";
import { getClients } from "../../services/clientService";
import MarkdownEditor from "../../components/MarkdownEditor";

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    client_id: "",
    start_date: "",
    end_date: "",
    status: "new",
    description: "",
  });

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    async function fetchData() {
      try {
        const [projectData, clientData] = await Promise.all([
          getProjectById(id),
          getClients(),
        ]);

        setFormData({
          name: projectData.name,
          client_id: projectData.clientInfo.id,
          status: projectData.status,
          start_date: projectData.startDate,
          end_date: projectData.deadline,
          description: projectData.description || "",
        });

        setClients(clientData);
      } catch (err) {
        setError("Failed to load project data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [id]);

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await updateProject(id, formData);
      navigate(`/projects/${id}`);
    } catch (err) {
      setError("Failed to update project.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return <div className="text-center text-gray-600">Loading...</div>;

  if (error)
    return <div className="text-center text-red-500">{error}</div>;

  return (
    <>
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Edit Project</h1>
          <p className="text-sm text-gray-500">
            Update project details and settings
          </p>
        </div>

        <Link
          to={`/projects/${id}`}
          className="text-sm text-blue-600 hover:underline"
        >
          ← Back
        </Link>
      </div>

      {/* FORM */}
      <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Project Title */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Project Title
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring focus:border-blue-400"
            />
          </div>

          {/* Client */}
          <div>
            <label className="block text-sm font-medium mb-1">Client</label>
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring focus:border-blue-400"
            >
              <option value="">Select a Client</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded focus:outline-none focus:ring focus:border-blue-400"
            >
              <option value="new">New</option>
              <option value="ongoing">On Going</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring focus:border-blue-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
                className="w-full border px-3 py-2 rounded focus:outline-none focus:ring focus:border-blue-400"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Description
            </label>
            <MarkdownEditor
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/projects/${id}`)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {submitting ? "Updating..." : "Update Project"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditProject;
